'use client';
import { useCallback, useMemo, useState } from 'react';
// import { v4 as uuidv4 } from 'uuid';
import { useI18n } from '@locales/client';
import { useSearchParams } from 'next/navigation';

import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { useGameSections } from '@/entities/game/model/hooks';
import { ConditionalWrapperGame } from '@/entities/game/ui/conditional-wrapper-game';
import { useIsAuth } from '@/shared/providers/user-provider/use-user';

import { BodyTextRegularPrimary } from '@/shared/ui/typography';
import { Box } from '@/shared/ui/box/Box';

import { NotFound, ScrollTrigger, Wrapper } from './styles';
import {
  Section,
  // SectionBlock,
  // SectionBlocks,
} from '@/widgets/game-section/ui/styles';

import { MobileSearchNotFound } from '@/widgets/game-sections/ui/section-mobile-search/mobile-search-not-found';
import { MobileSearchResult } from '@/widgets/game-sections/ui/section-mobile-search/mobile-search-result';

import { RenderCategories } from '@/widgets/game-sections/ui/render-categories';
import { GameSectionsSkeleton } from '@/widgets/game-sections/ui/game-sections-skeleton';
import { useInView } from '@/shared/hooks/useInView';
import { brandIsPh } from '@/shared/config/brand-by-target.mjs';
import { RecordWinSection } from '@/widgets/game-sections/ui/record-win-section';
import { JackpotSection } from '@/widgets/game-sections/ui/jackpot-section';
import { useGameNavigation } from '@/shared/hooks/useGameNavigation';
import { EMPTY_ARRAY } from '@/widgets/game-sections/ui/empty-values';

const ITEMS_PER_PAGE = 80;
const PAGER_IN_VIEW_OPTIONS = { threshold: 0.1, rootMargin: '100px' };

interface GameSectionsProps {
  openNotAllowedModal?: () => void;
  isGames?: boolean;
  isMobileMenu?: boolean;
}

const GameSections = ({
  openNotAllowedModal,
  isGames,
  isMobileMenu,
}: GameSectionsProps) => {
  const gameSectionsData = useGameSections();
  const {
    showCategories,
    games = EMPTY_ARRAY,
    gamesData,
    categoriesWithGames,
    recommendedGame,
    preferredGame,
    newGame,
    isSectionsLoading,
    activeBonusId,
  } = gameSectionsData;
  const [page, setPage] = useState(1);
  const loadNextPage = useCallback(() => {
    if (ITEMS_PER_PAGE * page < games.length) setPage((prev) => prev + 1);
  }, [page, games.length]);
  const [pagerRef] = useInView(PAGER_IN_VIEW_OPTIONS, loadNextPage, false);
  const isAuth = useIsAuth();

  const items = useMemo(
    () => games.slice(0, page * ITEMS_PER_PAGE),
    [games, page]
  );
  const searchParams = useSearchParams();
  const t = useI18n();

  const isMobile = useIsMobile();
  const { onGameClick } = useGameNavigation({ isMobile, isAuth });

  if (isMobileMenu) {
    if (!searchParams.get('search')) {
      return;
    }

    if (games.length === 0) {
      return <MobileSearchNotFound />;
    }

    return <MobileSearchResult items={items} onGameClick={onGameClick} />;
  }

  if (isSectionsLoading) {
    return <GameSectionsSkeleton />;
  }

  if (showCategories) {
    return (
      <Wrapper $hasScroll={true}>
        <RenderCategories
          isGames={isGames}
          openNotAllowedModal={openNotAllowedModal}
          setCurrentGame={onGameClick}
          categoriesWithGames={categoriesWithGames}
          recommendedGame={recommendedGame}
          preferredGame={preferredGame}
          newGame={newGame}
          totalGamesCount={gamesData?.length}
          activeBonusId={activeBonusId}
        />

        <RecordWinSection />
        <JackpotSection />
      </Wrapper>
    );
  }

  if (games.length === 0) {
    return (
      <Wrapper $hasScroll={true}>
        <NotFound>
          <BodyTextRegularPrimary color="#AFAFAF">
            {t('common.not_found')}
          </BodyTextRegularPrimary>
        </NotFound>

        <RenderCategories
          isGames={isGames}
          openNotAllowedModal={openNotAllowedModal}
          setCurrentGame={onGameClick}
          categoriesWithGames={categoriesWithGames}
          recommendedGame={recommendedGame}
          preferredGame={preferredGame}
          newGame={newGame}
          totalGamesCount={gamesData?.length}
          isNotFound
          activeBonusId={activeBonusId}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Box $margin={[0, brandIsPh ? '0 12px' : '0 18px']}>
        <Section $hasScroll={false}>
          {items?.map((game, index) => (
            <ConditionalWrapperGame
              key={game.id}
              game={game}
              handleClickGame={onGameClick}
              hasScroll={false}
              activeBonusId={activeBonusId}
              priority={index < 6}
            />
          ))}
        </Section>
        <ScrollTrigger ref={pagerRef} />
      </Box>
    </Wrapper>
  );
};

export { GameSections };
